import {
  Investigation,
  InvestigationDocumentRedriveState,
} from "../../lusca-search-api/generated-src";
import { STATUS_LABELS } from "./investigationUtils";

/**
 * The document-redrive path (see InvestigationDocumentRedriveState): step 1 re-processes the
 * transactions through the order redrive, the owner checks the CBEs, step 2 is a DLQ redrive
 * that clears the items. The stored status stays READY_TO_REDRIVE / EXECUTING_REDRIVE; these
 * helpers derive the extra labels and steps from `investigation.documentRedrive`.
 */
export type DocumentRedrive =
  | InvestigationDocumentRedriveState
  | null
  | undefined;

export const READY_FOR_DLQ_LABEL = "Ready to redrive (DLQ)";
export const DOCUMENT_REDRIVE_RUNNING_LABEL = "Order redrive running";

/** Step 1 is running (case EXECUTING_REDRIVE). */
export const documentRedriveRunning = (
  status: string | null | undefined,
  doc: DocumentRedrive
) => status === "EXECUTING_REDRIVE" && doc?.stage === "RUNNING";

/** Step 1 finished; the DLQ redrive (step 2) is next. */
export const readyForDlq = (
  status: string | null | undefined,
  doc: DocumentRedrive
) =>
  status === "READY_TO_REDRIVE" &&
  (doc?.stage === "AWAITING_CONFIRMATION" || doc?.stage === "CONFIRMED");

/** The owner still has to confirm the CBEs before the DLQ redrive unlocks. */
export const awaitingConfirmation = (
  inv: Pick<Investigation, "status" | "documentRedrive">
) =>
  inv.status === "READY_TO_REDRIVE" &&
  inv.documentRedrive?.stage === "AWAITING_CONFIRMATION";

/** Status label that says where on the document-redrive path a case is. */
export const statusLabel = (
  status: string | null | undefined,
  doc?: DocumentRedrive
): string => {
  if (!status) return "Unknown";
  if (documentRedriveRunning(status, doc))
    return DOCUMENT_REDRIVE_RUNNING_LABEL;
  if (readyForDlq(status, doc)) return READY_FOR_DLQ_LABEL;
  return STATUS_LABELS[status] || status;
};

/** "2,431 redriven · 12 failed" for a finished order redrive. */
export const documentRedriveSummary = (doc: DocumentRedrive): string => {
  if (!doc) return "";
  if (doc.stage === "FAILED") return "Failed";
  if (doc.processedCount == null) return "";
  const failed = doc.failedCount ?? 0;
  const ok = Math.max(0, doc.processedCount - failed);
  return [
    `${ok.toLocaleString()} redriven`,
    failed ? `${failed.toLocaleString()} failed` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};
